import React, { useState, useEffect } from 'react';
import {
  KatexPreview as SharedKatexPreview,
  KatexPreviewPanel as SharedKatexPreviewPanel,
  InlineKatexPreview as SharedInlineKatexPreview
} from '@bayesprep/shared-ui/katex/components';
import { getImageUrl, getImageUrlSync } from '../../utils/imageUtils';

/**
 * Individual KaTeX preview component with always-visible preview
 */
export const KatexPreview = (props) => {
  return (
    <SharedKatexPreview
      {...props}
      portal="SUPER_ADMIN"
    />
  );
};

/**
 * Comprehensive preview panel for questions with options and explanation
 * Handles image URL conversion for super admin portal
 */
export const KatexPreviewPanel = (props) => {
  const [imageUrls, setImageUrls] = useState({});
  const [loading, setLoading] = useState(true);

  // Convert relative image paths to accessible URLs
  useEffect(() => {
    const loadImages = async () => {
      const urls = {};

      // Load question image
      if (props.questionImageUrl) {
        // Set immediate sync URL first
        urls.questionImageUrl = getImageUrlSync(props.questionImageUrl);
        try {
          const url = await getImageUrl(props.questionImageUrl);
          if (url) {
            urls.questionImageUrl = url;
            setImageUrls(prev => ({ ...prev, questionImageUrl: url }));
          }
        } catch (error) {
          console.warn('Failed to load question image:', error);
        }
      }

      // Load explanation image
      if (props.explanationImageUrl) {
        // Set immediate sync URL first
        urls.explanationImageUrl = getImageUrlSync(props.explanationImageUrl);
        try {
          const url = await getImageUrl(props.explanationImageUrl);
          if (url) {
            urls.explanationImageUrl = url;
            setImageUrls(prev => ({ ...prev, explanationImageUrl: url }));
          }
        } catch (error) {
          console.warn('Failed to load explanation image:', error);
        }
      }

      // Load option images with immediate sync URLs first
      if (props.options) {
        console.log('Loading option images for live preview:', props.options.map(opt => ({ text: opt.text?.substring(0, 20), imageUrl: opt.imageUrl })));

        // Set immediate sync URLs first
        const optionsWithSyncUrls = props.options.map((option, index) => {
          if (option.imageUrl) {
            const syncUrl = getImageUrlSync(option.imageUrl);
            console.log(`Option ${index} immediate URL: ${syncUrl}`);
            return { ...option, imageUrl: syncUrl };
          }
          return option;
        });
        urls.options = optionsWithSyncUrls;

        // Set initial state with sync URLs
        setImageUrls(urls);
        setLoading(false);

        // Then load async URLs and update
        const optionsWithAsyncUrls = await Promise.all(
          props.options.map(async (option, index) => {
            if (option.imageUrl) {
              try {
                console.log(`Loading option ${index} image: ${option.imageUrl}`);
                const url = await getImageUrl(option.imageUrl);
                console.log(`Option ${index} image URL resolved to: ${url}`);
                return { ...option, imageUrl: url || getImageUrlSync(option.imageUrl) };
              } catch (error) {
                console.warn(`Failed to load option ${index} image:`, error);
                return { ...option, imageUrl: getImageUrlSync(option.imageUrl) };
              }
            }
            return option;
          })
        );

        console.log('Final options with resolved URLs:', optionsWithAsyncUrls.map(opt => ({ text: opt.text?.substring(0, 20), imageUrl: opt.imageUrl })));
        setImageUrls(prev => ({ ...prev, options: optionsWithAsyncUrls }));
        return; // Early return since we already set loading to false
      }

      setImageUrls(urls);
      setLoading(false);
    };

    setLoading(true);
    loadImages();
  }, [props.questionImageUrl, props.explanationImageUrl, JSON.stringify(props.options?.map(opt => opt.imageUrl))]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-4 bg-slate-200 rounded w-1/4 mb-2"></div>
          <div className="h-20 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <SharedKatexPreviewPanel
      {...props}
      questionImageUrl={imageUrls.questionImageUrl}
      explanationImageUrl={imageUrls.explanationImageUrl}
      options={imageUrls.options || props.options}
      portal="SUPER_ADMIN"
    />
  );
};

/**
 * Simple inline preview component for smaller spaces
 */
export const InlineKatexPreview = (props) => {
  return <SharedInlineKatexPreview {...props} />;
};
